import { useEffect } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import {
  canAccessUrl,
  getStepFromUrl,
  getUrlFromStep,
  getProcessState,
} from '@/services/navigation'

/**
 * Hook do blokowania nawigacji wstecz w przeglądarce
 * Używany podczas krytycznych etapów procesu podpisywania dokumentów
 */
export function usePreventBackNavigation(enabled: boolean = true) {
  useEffect(() => {
    if (!enabled) return

    // Dodaj entry do history, aby użytkownik nie mógł się cofnąć
    const preventBack = () => {
      window.history.pushState(null, '', window.location.href)
    }

    // Obsłuż próbę cofnięcia się
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault()
      // Przywróć aktualną pozycję w historii
      window.history.pushState(null, '', window.location.href)
    }

    // Dodaj entry do history przy inicjalizacji
    preventBack()

    // Nasłuchuj na próby nawigacji wstecz
    window.addEventListener('popstate', handlePopState)

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [enabled])
}

/**
 * Hook chroniący przed nieautoryzowaną nawigacją
 * Sprawdza czy użytkownik może uzyskać dostęp do danej strony
 * i przekierowuje na odpowiednią stronę jeśli nie
 *
 * WAŻNE: Używa window.history.replaceState() zamiast pushState()
 * aby wyczyścić historię przeglądarki i zablokować przycisk "wstecz"
 *
 * Wyjątek: /summary z returnToken - dozwolone (walidacja w ProtectedRoute)
 */
export function useNavigationGuard() {
  const [location, setLocation] = useLocation()

  useEffect(() => {
    // Check for /summary with returnToken - allow access (validation handled by ProtectedRoute)
    const urlParams = new URLSearchParams(window.location.search)
    const returnToken = urlParams.get('returnToken')

    if (location === '/summary' && returnToken) {
      // eslint-disable-next-line no-console
      console.log(
        'NavigationGuard: /summary z returnToken - pominięcie walidacji nawigacji'
      )
      return
    }

    // Sprawdź czy obecny URL jest dozwolony
    if (!canAccessUrl(location)) {
      console.warn(`Próba dostępu do niedozwolonej ścieżki: ${location}`)

      // Przekieruj na aktualny krok w procesie
      const state = getProcessState()
      const correctUrl = getUrlFromStep(state.currentStep)

      if (correctUrl !== location) {
        console.log(`Przekierowanie na dozwolną ścieżkę: ${correctUrl}`)
        // Wyczyść historię przy automatycznym przekierowaniu
        window.history.replaceState(null, '', correctUrl)
        setLocation(correctUrl)
      }
    }
  }, [location, setLocation])

  // Funkcja do bezpiecznej nawigacji - używana przez przyciski
  const navigateTo = (targetUrl: string): boolean => {
    // Check for /summary with returnToken - allow navigation (validation handled by ProtectedRoute)
    const urlParams = new URLSearchParams(
      targetUrl.includes('?') ? targetUrl.split('?')[1] : ''
    )
    const returnToken = urlParams.get('returnToken')

    const performNavigation = (url: string) => {
      window.history.replaceState(null, '', url)
      setLocation(url)
      window.scrollTo({ top: 0, behavior: 'instant' })
    }

    if (targetUrl.startsWith('/summary') && returnToken) {
      // eslint-disable-next-line no-console
      console.log(
        'NavigationGuard: navigateTo /summary z returnToken - dozwolone'
      )
      performNavigation(targetUrl)
      return true
    }

    const targetStep = getStepFromUrl(targetUrl)

    if (!targetStep) {
      console.error(`Nieznana ścieżka: ${targetUrl}`)
      return false
    }

    if (canAccessUrl(targetUrl)) {
      performNavigation(targetUrl)
      return true
    } else {
      console.warn(`Nawigacja zabroniona do: ${targetUrl}`)
      return false
    }
  }

  return { navigateTo }
}
